import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  ttl: number; // ms before auto-dismiss
}

interface ToasterContextValue {
  push: (message: string, variant?: ToastVariant, ttl?: number) => void;
  success: (message: string, ttl?: number) => void;
  error: (message: string, ttl?: number) => void;
  info: (message: string, ttl?: number) => void;
}

// ---------------------------------------------------------------------------
// Context + hook
// ---------------------------------------------------------------------------

const ToasterContext = createContext<ToasterContextValue | null>(null);

export const useToast = (): ToasterContextValue => {
  const ctx = useContext(ToasterContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToasterProvider>');
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider + UI
// ---------------------------------------------------------------------------

const VARIANT_STYLES: Record<ToastVariant, { icon: string; classes: string }> = {
  success: {
    icon: '✓',
    classes: 'bg-ok-bg border-line text-ok-fg',
  },
  error: {
    icon: '✗',
    classes: 'bg-fail-bg border-line text-fail-fg',
  },
  info: {
    icon: 'ℹ',
    classes: 'bg-tag-bg border-accent text-link',
  },
};

export const ToasterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, variant: ToastVariant = 'info', ttl?: number) => {
      const id = nextId.current++;
      const t: Toast = {
        id,
        message,
        variant,
        ttl: ttl ?? (variant === 'error' ? 6000 : 3500),
      };
      setToasts((prev) => [...prev, t]);
    },
    [],
  );

  const value: ToasterContextValue = {
    push,
    success: (m, ttl) => push(m, 'success', ttl),
    error: (m, ttl) => push(m, 'error', ttl),
    info: (m, ttl) => push(m, 'info', ttl),
  };

  return (
    <ToasterContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToasterContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({
  toast,
  onDismiss,
}) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, toast.ttl);
    return () => clearTimeout(t);
  }, [toast.ttl, onDismiss]);

  const style = VARIANT_STYLES[toast.variant];
  return (
    <div
      role="status"
      className={`pointer-events-auto min-w-[18rem] max-w-md border rounded-lg shadow-sm px-4 py-3 text-sm animate-[fadeIn_120ms_ease-out] ${style.classes}`}
    >
      <div className="flex items-start">
        <span className="mr-2 text-base leading-5">{style.icon}</span>
        <span className="flex-1 break-words">{toast.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="-my-1 -mr-1 ml-2 rounded px-1.5 text-xs opacity-70 hover:opacity-100 focus-visible:outline focus-visible:outline-2"
        >
          ✕
        </button>
      </div>
    </div>
  );
};
